import { colorAccent1 } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  DataObjectGroupProcessingStateType,
  DirectoryTableProps,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  getDayjs,
  getDayjsNow,
  InputDataActiveInfoParams,
  Table,
  TextEllipsis,
  useSystemClassifierItemsBy,
  useSystemClassifiers,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { type FC, useMemo } from 'react';
import { AppLink } from '../../common/AppLink';
import {
  ActiveProcessingType,
  getActiveProcessingTypeLabel,
} from './ActiveProcessingType';
import { CollectionRatio } from './CollectionRatio';
import { ErrorLevel } from './ErrorLevel';
import { InputDataActionCell } from './InputDataActionCell';
import translations from './InputDataDirectoryTable.translations.json';
import { getRowIdAndType } from './utils';
import './table/filterInputs';
import { RelationsTablesModalParams } from '../relations/RelationsTablesModal';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-directory-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<DataObjectGroupProcessingStateType>();

export type InputDataDirectoryTableProps =
  DirectoryTableProps<DataObjectGroupProcessingStateType> & {
    onShowActiveInputInfo: (params: InputDataActiveInfoParams) => void;
    onActionSuccess?: () => unknown;
    onShowRelationsTables?: (params: RelationsTablesModalParams) => void;
    onActionDropdownOpenChange?: (open: boolean) => void;
  };

export function InputDataDirectoryTable(props: InputDataDirectoryTableProps) {
  const {
    onShowActiveInputInfo,
    onActionSuccess,
    onShowRelationsTables,
    onActionDropdownOpenChange,
  } = props;
  const { t } = useTranslation();
  const {
    DOMAIN: domainOptions,
    FREQUENCY: frequencyOptions,
    PROCESSING_ERROR_LEVEL: errorOptions,
  } = useSystemClassifierSelectOptions([
    'DOMAIN',
    'FREQUENCY',
    'PROCESSING_ERROR_LEVEL',
  ]);

  const {
    PROCESSING_TYPE: { systemClassifierItems: processingTypes },
    PROCESSING_METHOD: { systemClassifierItems: processingMethods },
    DATA_PROCESSING_SCOPE: { systemClassifierItems: processingScopes },
    PROCESSING_ERROR_LEVEL: { systemClassifierItems: processingErrorLevels },
    PROCESSING_STATUS: { systemClassifierItems: processingStatuses },
  } = useSystemClassifiers(
    'PROCESSING_TYPE',
    'PROCESSING_METHOD',
    'DATA_PROCESSING_SCOPE',
    'PROCESSING_ERROR_LEVEL',
    'PROCESSING_STATUS',
  );

  const { DOMAIN: domainsById, FREQUENCY: frequenciesById } =
    useSystemClassifierItemsBy(['DOMAIN', 'FREQUENCY'], 'id');

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (rowData) =>
          rowData.domainId ? domainsById.get(rowData.domainId)?.name : '',
        {
          id: 'domainId',
          header: t('column.domain'),
          filterFn: 'includesOption',
          meta: {
            className: 'sticky-left',
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          formatCodeName({
            code: rowData.dataObjectGroupCode,
            name: rowData.dataObjectGroupName,
          }),
        {
          id: 'name',
          header: t('column.name'),
          cell: (props) => (
            <TextEllipsis>
              <Link {...props} />
            </TextEllipsis>
          ),
          filterFn: 'inputSampleSelect',
          meta: {
            className: 'sticky-left',
            width: '25ch',
          },
        },
      ),
      columnHelper.accessor('valueDate', {
        header: t('column.valueDate'),
        cell: (props) => (
          <TextEllipsis>
            {formatDate(props.row.original.valueDate)}
          </TextEllipsis>
        ),
        filterFn: 'valueDateSelect',
        meta: {
          width: '18ch',
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.frequencyId
            ? frequenciesById.get(rowData.frequencyId)?.name
            : '',
        {
          id: 'frequencyId',
          header: t('column.frequency'),
          filterFn: 'includesOption',
          meta: {
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor('responsibleUserUsername', {
        id: 'username',
        header: t('column.responsible'),
        meta: {
          width: '16ch',
        },
      }),
      columnHelper.accessor((rowData) => rowData.dueDateTime, {
        id: 'dueDateTime',
        header: t('column.dueDateTime'),
        cell: (props) => {
          const { dueDateTime, collectionRatio, processing } =
            props.row.original;
          const isWarning =
            dueDateTime &&
            getDayjs(dueDateTime)?.isBefore(getDayjsNow()) &&
            !collectionRatio &&
            processing === null;

          return (
            <TextEllipsis
              style={{ color: isWarning ? colorAccent1 : undefined }}
            >
              {formatDateTimeShort(props.row.original.dueDateTime)}
            </TextEllipsis>
          );
        },
        filterFn: 'validPeriod',
        meta: {
          width: '16ch',
        },
      }),
      columnHelper.accessor('collectionRatio', {
        header: t('column.collectionRatio'),
        cell: (props) => (
          <CollectionRatio processingState={props.row.original} />
        ),
        enableColumnFilter: false,
        meta: {
          width: '14ch',
        },
      }),
      columnHelper.accessor(
        (rowData) => formatDateTimeShort(rowData.lastProcessingEndTime),
        {
          id: 'lastProcessingEndTime',
          header: t('column.lastProcessingEndTime'),
          enableColumnFilter: false,
          meta: {
            width: '24ch',
          },
        },
      ),
      columnHelper.accessor('errorLevelId' as any, {
        header: t('column.errors'),
        cell: (props) => (
          <ErrorLevel
            processingState={props.row.original}
            processingErrorLevels={processingErrorLevels}
            processingTypes={processingTypes}
            processingStatuses={processingStatuses}
          />
        ),
        filterFn: 'includesOption',
        enableSorting: false,
        meta: {
          width: '11ch',
        },
      }),
      columnHelper.accessor(
        (processingState) =>
          getActiveProcessingTypeLabel({
            processingState,
            processingTypes,
            processingMethods,
            processingScopes,
          }),
        {
          id: 'processing',
          header: t('column.activeProcessingState'),
          cell: ActiveProcessingType,
          enableColumnFilter: false,
          meta: {
            width: '18ch',
          },
        },
      ),
      columnHelper.display({
        id: 'operations',
        header: t('column.operations'),
        cell: (props) => (
          <InputDataActionCell
            {...props}
            onShowActiveInputInfo={onShowActiveInputInfo}
            onActionSuccess={onActionSuccess}
            onShowRelationsTables={onShowRelationsTables}
            onDropdownOpenChange={onActionDropdownOpenChange}
          />
        ),
        meta: {
          width: '17ch',
        },
      }),
    ],
    [
      domainsById,
      frequenciesById,
      onActionSuccess,
      onActionDropdownOpenChange,
      onShowActiveInputInfo,
      onShowRelationsTables,
      processingErrorLevels,
      processingMethods,
      processingScopes,
      processingStatuses,
      processingTypes,
      t,
    ],
  );
  const options = useMemo(
    () => ({
      domainId: domainOptions.map((domain) => ({
        id: domain.value,
        name: domain.label,
      })),
      frequencyId: frequencyOptions.map((frequency) => ({
        id: frequency.value,
        name: frequency.label,
      })),
      errorLevelId: errorOptions.map((error) => ({
        id: error.value,
        name: error.label,
      })),
    }),
    [domainOptions, errorOptions, frequencyOptions],
  );

  return (
    <Table
      name="input-data-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      options={options}
      scrollContainerSelector="#input-data-directory-content"
      sticky
      virtual
      {...props}
    />
  );
}

const Link: FC<CellContext<DataObjectGroupProcessingStateType, any>> = ({
  row,
}) => {
  const { id, type, name } = getRowIdAndType(row.original);
  return (
    <AppLink
      id={`sample-${type}-${id}`}
      to={`/data/samples/${id}?date=${row.original.valueDate}&type=${type}`}
    >
      {name}
    </AppLink>
  );
};
